import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";

/**
 * PasswordResetForm - Password reset request component replacing account/password_reset.html
 *
 * This component provides the password reset request interface with:
 * - Email input for password reset
 * - Error handling and validation
 * - Loading states
 * - Success confirmation
 * - Links back to login
 */
const PasswordResetForm: React.FC = () => {
  const { requestPasswordReset, authState, clearAuthState } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  // Clear auth state on component mount
  useEffect(() => {
    clearAuthState();
  }, [clearAuthState]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      return;
    }

    const result = await requestPasswordReset(formData.email);
    if (result.success) {
      setIsSubmitted(true);
    }
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          maxWidth: 480,
          mx: 'auto',
          py: { xs: 2, sm: 4 },
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        <Card>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              Check Your Email
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We've sent a password reset link to{" "}
              <strong>{formData.email}</strong>
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              If you don't see the email in your inbox, please check your spam
              folder.
            </Typography>

            <Button
              component={RouterLink}
              to="/auth/login"
              variant="contained"
              fullWidth
              size="large"
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        py: { xs: 2, sm: 4 },
        minWidth: 0,
        overflowWrap: 'anywhere',
      }}
    >
      <Card>
        <CardContent>
          {/* Header */}
          <Typography
            variant="h4"
            component="h1"
            align="center"
            gutterBottom
            fontWeight="bold"
          >
            Reset Password
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 3 }}
          >
            Enter your email address and we'll send you a link to reset your
            password
          </Typography>

          {/* Reset Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
            />

            {/* Error Display */}
            {authState.error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {authState.error}
              </Alert>
            )}

            {/* Success Display */}
            {authState.success && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                {authState.success}
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={authState.isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {authState.isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </Box>

          {/* Back to Login */}
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Remember your password?{" "}
              <Link
                component={RouterLink}
                to="/auth/login"
                color="primary"
                sx={{ textDecoration: "none", fontWeight: "medium" }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PasswordResetForm;
